import { LinearGradient } from "expo-linear-gradient";
import { Platform } from "react-native";
import { ClipPath, ForeignObject, G, Path, Svg } from "react-native-svg";
import { SizeTokens, ViewProps, useTheme } from "tamagui";

import { getTokenValueOrFallback } from "@/theme/helpers";
import { RotationWrapper } from "./RotationWrapper";

export default function LoadingSpinner({
  size = "$225",
  gradientStartOverride,
  gradientEndOverride,
  ...rest
}: ViewProps & {
  size?: SizeTokens;
  gradientStartOverride?: string;
  gradientEndOverride?: string;
}) {
  const theme = useTheme();
  const gradientStart = gradientStartOverride ?? theme.backgroundFill?.get();
  const gradientEnd = gradientEndOverride ?? theme.fillPrimary?.get();
  const spinnerSize = getTokenValueOrFallback(size);

  return (
    <RotationWrapper {...rest}>
      <Svg
        role="progressbar"
        aria-label="Loading"
        width={spinnerSize}
        height={spinnerSize}
        viewBox="0 0 80 80"
        fill="none"
      >
        {/* Background circle */}
        <Path
          d="M76 40C76 44.7276 75.0688 49.4089 73.2597 53.7766C71.4505 58.1443 68.7988 62.1129 65.4558 65.4558C62.1129 68.7988 58.1443 71.4505 53.7766 73.2597C49.4089 75.0688 44.7276 76 40 76C35.2724 76 30.5911 75.0688 26.2234 73.2597C21.8557 71.4505 17.8871 68.7988 14.5442 65.4558C11.2012 62.1129 8.5495 58.1443 6.74034 53.7766C4.93117 49.4089 4 44.7276 4 40C4 35.2724 4.93117 30.5911 6.74034 26.2234C8.54951 21.8557 11.2012 17.8871 14.5442 14.5442C17.8871 11.2012 21.8557 8.5495 26.2234 6.74034C30.5911 4.93117 35.2724 4 40 4C44.7276 4 49.4089 4.93117 53.7766 6.74034C58.1443 8.54951 62.1129 11.2013 65.4558 14.5442C68.7988 17.8871 71.4505 21.8557 73.2597 26.2234C75.0688 30.5911 76 35.2724 76 40L76 40Z"
          stroke={theme.contentLoaderBackground?.get()}
          strokeWidth="8"
        />

        {/* Animated gradient circle */}
        <G clipPath="url(#nori_loading_spinner_clip_path)">
          <G transform="matrix(0 0.036 -0.036 0 40 40)">
            <ForeignObject
              x="-1222.22"
              y="-1222.22"
              width="2444.44"
              height="2444.44"
            >
              {Platform.OS === "web" ? (
                <div
                  style={{
                    background: `conic-gradient(from 90deg,${gradientStart} 0deg,${gradientEnd} 360deg)`,
                    height: "100%",
                    width: "100%",
                    opacity: 1,
                  }}
                ></div>
              ) : (
                <LinearGradient
                  style={{ height: "100%", width: "100%" }}
                  colors={[gradientStart, gradientEnd]}
                />
              )}
            </ForeignObject>
          </G>
        </G>
      </Svg>
    </RotationWrapper>
  );
}

// Export this so it can preload before the loading spinner is needed
export const LoadingSpinnerClipPath = () => (
  <Svg width={0} height={0} opacity={0}>
    <ClipPath id="nori_loading_spinner_clip_path">
      <Path d="M73.2597 53.7766L69.5641 52.2459L73.2597 53.7766ZM65.4558 65.4558L62.6274 62.6274L65.4558 65.4558ZM53.7766 73.2597L52.2459 69.5641L53.7766 73.2597ZM40 76V72V76ZM26.2234 73.2597L27.7541 69.5641L26.2234 73.2597ZM14.5442 65.4558L17.3726 62.6274L14.5442 65.4558ZM6.74034 53.7766L10.4359 52.2459L6.74034 53.7766ZM4 40H8H4ZM6.74034 26.2234L10.4359 27.7541L6.74034 26.2234ZM14.5442 14.5442L17.3726 17.3726L14.5442 14.5442ZM26.2234 6.74034L27.7541 10.4359L26.2234 6.74034ZM40 4V8V4ZM53.7766 6.74034L52.2459 10.4359L53.7766 6.74034ZM65.4558 14.5442L62.6274 17.3726L65.4558 14.5442ZM73.2597 26.2234L69.5641 27.7541L73.2597 26.2234ZM76 40H72C72 44.2023 71.1723 48.3635 69.5641 52.2459L73.2597 53.7766L76.9552 55.3073C78.9654 50.4543 80 45.2529 80 40H76ZM73.2597 53.7766L69.5641 52.2459C67.956 56.1283 65.5989 59.6559 62.6274 62.6274L65.4558 65.4558L68.2843 68.2843C71.9986 64.5699 74.945 60.1604 76.9552 55.3073L73.2597 53.7766ZM65.4558 65.4558L62.6274 62.6274C59.6559 65.5989 56.1283 67.956 52.2459 69.5641L53.7766 73.2597L55.3073 76.9552C60.1604 74.945 64.5699 71.9986 68.2843 68.2843L65.4558 65.4558ZM53.7766 73.2597L52.2459 69.5641C48.3634 71.1723 44.2023 72 40 72V76V80C45.2529 80 50.4543 78.9654 55.3073 76.9552L53.7766 73.2597ZM40 76V72C35.7977 72 31.6365 71.1723 27.7541 69.5641L26.2234 73.2597L24.6927 76.9552C29.5457 78.9654 34.7471 80 40 80V76ZM26.2234 73.2597L27.7541 69.5641C23.8717 67.956 20.3441 65.5989 17.3726 62.6274L14.5442 65.4558L11.7157 68.2843C15.4301 71.9986 19.8396 74.945 24.6927 76.9552L26.2234 73.2597ZM14.5442 65.4558L17.3726 62.6274C14.4011 59.6559 12.044 56.1283 10.4359 52.2459L6.74034 53.7766L3.04482 55.3073C5.05501 60.1604 8.00138 64.5699 11.7157 68.2843L14.5442 65.4558ZM6.74034 53.7766L10.4359 52.2459C8.8277 48.3634 8 44.2023 8 40H4H0C-4.76837e-07 45.2529 1.03463 50.4543 3.04482 55.3073L6.74034 53.7766ZM4 40H8C8 35.7977 8.82771 31.6365 10.4359 27.7541L6.74034 26.2234L3.04482 24.6927C1.03463 29.5457 4.76837e-07 34.7471 0 40H4ZM6.74034 26.2234L10.4359 27.7541C12.044 23.8717 14.4011 20.3441 17.3726 17.3726L14.5442 14.5442L11.7157 11.7157C8.00139 15.4301 5.05501 19.8396 3.04482 24.6927L6.74034 26.2234ZM14.5442 14.5442L17.3726 17.3726C20.3441 14.4011 23.8717 12.044 27.7541 10.4359L26.2234 6.74034L24.6927 3.04482C19.8396 5.055 15.4301 8.00138 11.7157 11.7157L14.5442 14.5442ZM26.2234 6.74034L27.7541 10.4359C31.6366 8.8277 35.7977 8 40 8V4V0C34.7471 -4.76837e-07 29.5457 1.03463 24.6927 3.04482L26.2234 6.74034ZM40 4V8C44.2023 8 48.3635 8.82771 52.2459 10.4359L53.7766 6.74034L55.3073 3.04482C50.4543 1.03463 45.2529 7.15256e-07 40 0V4ZM53.7766 6.74034L52.2459 10.4359C56.1283 12.044 59.6559 14.4011 62.6274 17.3726L65.4558 14.5442L68.2843 11.7157C64.5699 8.00139 60.1604 5.05501 55.3073 3.04482L53.7766 6.74034ZM65.4558 14.5442L62.6274 17.3726C65.5989 20.3441 67.956 23.8717 69.5641 27.7541L73.2597 26.2234L76.9552 24.6927C74.945 19.8396 71.9986 15.4301 68.2843 11.7157L65.4558 14.5442ZM73.2597 26.2234L69.5641 27.7541C71.1723 31.6366 72 35.7977 72 40L76 40H80C80 34.7471 78.9654 29.5457 76.9552 24.6927L73.2597 26.2234ZM76 40H80V40H76H72V40H76Z" />
    </ClipPath>
  </Svg>
);
